import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Minimize2, Maximize2, User, Bot, Ticket } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ChatWidget = () => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // UI i18n
  const i18n = {
    es: { greeting: '¡Hola! 👋 Soy el asistente virtual de Purolink. ¿En qué puedo ayudarte hoy?', title: 'Chat de Soporte', placeholder: 'Escribe tu mensaje...', error: 'Lo siento, ha ocurrido un error. ¿Quieres crear un ticket de soporte para que te contactemos?', ticketTitle: '🎫 Crear Ticket de Soporte', ticketName: 'Tu nombre *', ticketEmail: 'Tu email *', ticketOrder: 'Nº de pedido (opcional)', ticketCreate: 'Crear Ticket', cancel: 'Cancelar', minimized: 'Chat de Soporte', errFields: 'Por favor, introduce tu nombre y email', errTicket: 'Error al crear el ticket', ticketCreated: (n) => `Ticket #${n} creado` },
    en: { greeting: 'Hi! 👋 I am the Purolink virtual assistant. How can I help you today?', title: 'Support Chat', placeholder: 'Type your message...', error: 'Sorry, an error occurred. Would you like to create a support ticket so we can contact you?', ticketTitle: '🎫 Create Support Ticket', ticketName: 'Your name *', ticketEmail: 'Your email *', ticketOrder: 'Order number (optional)', ticketCreate: 'Create Ticket', cancel: 'Cancel', minimized: 'Support Chat', errFields: 'Please enter your name and email', errTicket: 'Error creating ticket', ticketCreated: (n) => `Ticket #${n} created` },
    de: { greeting: 'Hallo! 👋 Ich bin der virtuelle Assistent von Purolink. Wie kann ich Ihnen heute helfen?', title: 'Support-Chat', placeholder: 'Nachricht schreiben...', error: 'Entschuldigung, ein Fehler ist aufgetreten. Möchten Sie ein Support-Ticket erstellen?', ticketTitle: '🎫 Support-Ticket erstellen', ticketName: 'Ihr Name *', ticketEmail: 'Ihre E-Mail *', ticketOrder: 'Bestellnummer (optional)', ticketCreate: 'Ticket erstellen', cancel: 'Abbrechen', minimized: 'Support-Chat', errFields: 'Bitte geben Sie Ihren Namen und Ihre E-Mail ein', errTicket: 'Fehler beim Erstellen des Tickets', ticketCreated: (n) => `Ticket #${n} erstellt` },
    fr: { greeting: 'Bonjour ! 👋 Je suis l’assistant virtuel de Purolink. Comment puis-je vous aider aujourd’hui ?', title: 'Chat de support', placeholder: 'Écrivez votre message...', error: 'Désolé, une erreur est survenue. Voulez-vous créer un ticket de support ?', ticketTitle: '🎫 Créer un ticket de support', ticketName: 'Votre nom *', ticketEmail: 'Votre email *', ticketOrder: 'N° de commande (optionnel)', ticketCreate: 'Créer le ticket', cancel: 'Annuler', minimized: 'Chat de support', errFields: 'Veuillez saisir votre nom et votre email', errTicket: 'Erreur lors de la création du ticket', ticketCreated: (n) => `Ticket #${n} créé` },
    it: { greeting: 'Ciao! 👋 Sono l’assistente virtuale di Purolink. Come posso aiutarti oggi?', title: 'Chat di supporto', placeholder: 'Scrivi il tuo messaggio...', error: 'Mi dispiace, si è verificato un errore. Vuoi creare un ticket di supporto?', ticketTitle: '🎫 Crea ticket di supporto', ticketName: 'Il tuo nome *', ticketEmail: 'La tua email *', ticketOrder: 'N° ordine (opzionale)', ticketCreate: 'Crea ticket', cancel: 'Annulla', minimized: 'Chat di supporto', errFields: 'Inserisci il tuo nome e la tua email', errTicket: 'Errore nella creazione del ticket', ticketCreated: (n) => `Ticket #${n} creato` },
    pt: { greeting: 'Olá! 👋 Sou o assistente virtual da Purolink. Como posso ajudar hoje?', title: 'Chat de Suporte', placeholder: 'Escreva sua mensagem...', error: 'Desculpe, ocorreu um erro. Deseja criar um ticket de suporte?', ticketTitle: '🎫 Criar ticket de suporte', ticketName: 'Seu nome *', ticketEmail: 'Seu email *', ticketOrder: 'Nº do pedido (opcional)', ticketCreate: 'Criar ticket', cancel: 'Cancelar', minimized: 'Chat de Suporte', errFields: 'Por favor, informe seu nome e email', errTicket: 'Erro ao criar o ticket', ticketCreated: (n) => `Ticket #${n} criado` },
    zh: { greeting: '你好！👋 我是 Purolink 的虚拟助手。今天有什么可以帮您？', title: '客服聊天', placeholder: '输入您的消息...', error: '抱歉，出现了错误。要创建支持工单让我们联系您吗？', ticketTitle: '🎫 创建支持工单', ticketName: '您的姓名 *', ticketEmail: '您的邮箱 *', ticketOrder: '订单号（可选）', ticketCreate: '创建工单', cancel: '取消', minimized: '客服聊天', errFields: '请输入您的姓名和邮箱', errTicket: '创建工单出错', ticketCreated: (n) => `工单 #${n} 已创建` },
    ar: { greeting: 'مرحباً! 👋 أنا المساعد الافتراضي لـ Purolink. كيف يمكنني مساعدتك اليوم؟', title: 'دردشة الدعم', placeholder: 'اكتب رسالتك...', error: 'عذراً، حدث خطأ. هل تريد إنشاء تذكرة دعم لنتواصل معك؟', ticketTitle: '🎫 إنشاء تذكرة دعم', ticketName: 'اسمك *', ticketEmail: 'بريدك الإلكتروني *', ticketOrder: 'رقم الطلب (اختياري)', ticketCreate: 'إنشاء التذكرة', cancel: 'إلغاء', minimized: 'دردشة الدعم', errFields: 'يرجى إدخال اسمك وبريدك الإلكتروني', errTicket: 'حدث خطأ أثناء إنشاء التذكرة', ticketCreated: (n) => `تم إنشاء التذكرة #${n}` },
  };
  const T = i18n[language] || i18n.es;

  const [messages, setMessages] = useState([
    { role: 'assistant', content: T.greeting }
  ]);
  // When the user changes language and the chat hasn't been used yet, refresh the greeting.
  useEffect(() => {
    setMessages(prev => {
      if (prev.length === 1 && prev[0].role === 'assistant') {
        return [{ role: 'assistant', content: T.greeting }];
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    user_name: '',
    user_email: '',
    order_number: '',
    subject: ''
  });
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isMinimized]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/api/chat`, {
        message: userMessage,
        session_id: sessionId,
        user_name: ticketForm.user_name || null,
        user_email: ticketForm.user_email || null,
        language: language || 'es',
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: T.error
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const createTicket = async () => {
    if (!ticketForm.user_name || !ticketForm.user_email) {
      toast.error(T.errFields);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/api/chat/ticket`, {
        session_id: sessionId,
        user_name: ticketForm.user_name,
        user_email: ticketForm.user_email,
        order_number: ticketForm.order_number || null,
        subject: ticketForm.subject || 'Consulta de soporte'
      });

      toast.success(T.ticketCreated(response.data.ticket_number));
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `✅ ${response.data.message}`
      }]);
      setShowTicketForm(false);
    } catch (error) {
      console.error('Ticket error:', error);
      toast.error(T.errTicket);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-red-600 hover:bg-red-700 text-white rounded-full p-4 shadow-lg transition-all duration-300 hover:scale-110"
        aria-label="Abrir chat"
        data-testid="chat-widget-button"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    );
  }

  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-6 right-6 z-50 bg-white rounded-lg shadow-lg border cursor-pointer hover:shadow-xl transition-shadow"
        onClick={() => setIsMinimized(false)}
        data-testid="chat-widget-minimized"
      >
        <div className="flex items-center gap-2 px-4 py-3">
          <MessageCircle className="h-5 w-5 text-red-600" />
          <span className="font-medium text-sm">{T.minimized}</span>
          <Maximize2 className="h-4 w-4 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <Card 
      className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] shadow-2xl border-0"
      data-testid="chat-widget"
    >
      {/* Header */}
      <CardHeader className="bg-red-600 text-white rounded-t-lg py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            <CardTitle className="text-base font-medium">{T.title}</CardTitle>
          </div>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-white hover:bg-red-700"
              onClick={() => setIsMinimized(true)}
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-white hover:bg-red-700"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Messages */}
        <ScrollArea className="h-[350px] p-4">
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-4 w-4 text-red-600" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    msg.role === 'user'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <div className="chat-markdown leading-relaxed space-y-2">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({node, ...props}) => <p className="m-0 whitespace-pre-wrap" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc pl-5 space-y-1" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal pl-5 space-y-1" {...props} />,
                          li: ({node, ...props}) => <li className="leading-snug" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                          em: ({node, ...props}) => <em className="italic" {...props} />,
                          a: ({node, ...props}) => <a className="text-red-600 underline hover:text-red-700" target="_blank" rel="noopener noreferrer" {...props} />,
                          h1: ({node, ...props}) => <h3 className="text-base font-semibold mt-1 mb-1" {...props} />,
                          h2: ({node, ...props}) => <h3 className="text-base font-semibold mt-1 mb-1" {...props} />,
                          h3: ({node, ...props}) => <h3 className="text-sm font-semibold mt-1 mb-1" {...props} />,
                          code: ({inline, ...props}) => inline
                            ? <code className="bg-white/70 px-1 py-0.5 rounded text-[12px] font-mono" {...props} />
                            : <code className="block bg-white/70 px-2 py-1 rounded text-[12px] font-mono overflow-x-auto" {...props} />,
                          blockquote: ({node, ...props}) => <blockquote className="border-l-2 border-gray-300 pl-2 italic text-gray-600" {...props} />,
                          hr: () => <hr className="my-2 border-gray-300" />,
                          table: ({node, ...props}) => <table className="border-collapse text-xs my-1" {...props} />,
                          th: ({node, ...props}) => <th className="border border-gray-300 px-1.5 py-0.5 bg-gray-200 font-semibold" {...props} />,
                          td: ({node, ...props}) => <td className="border border-gray-300 px-1.5 py-0.5" {...props} />,
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <span className="whitespace-pre-wrap">{msg.content}</span>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-gray-600" />
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-2 justify-start">
                <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-red-600" />
                </div>
                <div className="bg-gray-100 rounded-lg px-3 py-2">
                  <div className="flex gap-1">
                    <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Ticket Form */}
        {showTicketForm && (
          <div className="border-t p-4 bg-yellow-50">
            <p className="text-sm font-medium text-yellow-800 mb-3">
              {T.ticketTitle}
            </p>
            <div className="space-y-2">
              <Input
                placeholder={T.ticketName}
                value={ticketForm.user_name}
                onChange={(e) => setTicketForm({ ...ticketForm, user_name: e.target.value })}
                className="h-9 text-sm"
              />
              <Input
                type="email"
                placeholder={T.ticketEmail}
                value={ticketForm.user_email}
                onChange={(e) => setTicketForm({ ...ticketForm, user_email: e.target.value })}
                className="h-9 text-sm"
              />
              <Input
                placeholder={T.ticketOrder}
                value={ticketForm.order_number}
                onChange={(e) => setTicketForm({ ...ticketForm, order_number: e.target.value })}
                className="h-9 text-sm"
              />
              <div className="flex gap-2">
                <Button
                  onClick={createTicket}
                  disabled={isLoading}
                  className="flex-1 h-9 bg-yellow-600 hover:bg-yellow-700"
                >
                  {T.ticketCreate}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowTicketForm(false)}
                  className="h-9"
                >
                  {T.cancel}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="border-t p-3">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 flex-shrink-0"
              onClick={() => setShowTicketForm(!showTicketForm)}
              title="Crear ticket de soporte"
            >
              <Ticket className="h-4 w-4" />
            </Button>
            <Input
              ref={inputRef}
              placeholder={T.placeholder}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              className="h-10"
              data-testid="chat-input"
            />
            <Button
              onClick={sendMessage}
              disabled={isLoading || !inputMessage.trim()}
              className="h-10 w-10 bg-red-600 hover:bg-red-700 p-0"
              data-testid="chat-send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChatWidget;
